import {
  ComponentProps,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import DataTable from "@/components/controls/DataTable/DataTable";
import {
  GroupRankResolvers,
  statusRank
} from "@/entities/dataTables/groupOrdering";
import DataTableToolbar from "@/components/controls/DataTableToolbar";
import ToolbarActions from "@/components/controls/ToolbarActions/ToolbarActions";
import ContractsBottomKPIBar from "@/components/controls/BottomKPIBar/ContractsBottomKPIBar";
import {
  SystemCurrencyCell,
  TermsCurrencyCell,
  systemCurrencyHeader,
  termsCurrencyHeader,
  useTermsColumnsVisibleByDefault
} from "@/entities/dataTables/currencyColumns";
import { useAccountCurrencyResolution } from "@/services/hooks/useCurrencyConversion";
import ContractStatusBadge, {
  getContractStatusIcon
} from "@/pages/contracts/ContractsTable/ContractStatusBadge";
import OnboardingStageCell from "@/pages/contracts/ContractsTable/OnboardingStageCell";
import FxRateCell from "@/pages/contracts/ContractsTable/FxRateCell";
import IconContainer from "@/components/ui/icon-container";
import Text from "@/components/ui/text";
import ConfidenceLevelIcon from "@/components/controls/ConfidenceLevelIcon";
import { useEntityExport } from "@/services/hooks/entities/useEntityExport";
import {
  ExportStateChangeHandler,
  useNotifyExportState
} from "@/services/hooks/entities/useExportButtonState";
import {
  ContractColumn,
  ContractRenewalType,
  ContractTableRow,
  ContractTableStatus,
  ENABLE_ONBOARDING_PHASE_KEY
} from "@/pages/contracts/types";
import { CsmRenewalConfidenceLevel } from "@/pages/deals/types";
import {
  buildDisplayValueById,
  groupFieldValues,
  buildGroupSummaries,
  rekeyGroupSummaries
} from "@/components/controls/DataTable/groupSummaries";
import {
  contractTableStatusColors,
  getContractStatusLabel,
  getContractStatusValue,
  getVisibleContractsGroupByOptions
} from "@/pages/contracts/helpers";
import {
  useContractFields,
  useFilteredContracts
} from "@/pages/contracts/hooks";
import { useDeals } from "@/pages/deals/hooks";
import { useFieldVisibility } from "@/pages/settings/hooks";
import { useOnboardingStages } from "@/pages/settings/components/contractsSettings/hooks";
import { displayTotalCurrency } from "@/utils/numbers";
import { formatFullDate, toDateOnlyISO } from "@/utils/datetime";
import { getEnumTKey } from "@/utils/strings";
import { tWithFallback } from "@/utils/i18n";
import { EmptyFilters, Filters } from "@/services/api/types";
import { ColumnData } from "@/entities/dataTables/types";
import { resolveSortField } from "@/entities/dataTables/sorting";
import { DataTableFilter } from "@/components/controls/DataTableFilters/DataTableFilters";
import {
  EmptyView,
  ViewDataConfiguration,
  ViewType
} from "@/entities/views/types";
import {
  getTempView,
  mapSelectedCustomColumnsToTableColumns,
  getDefaultColumnsStateWithCustomFields,
  mergeMissingDefaultColumns
} from "@/entities/dataTables/helpers";
import { SettingsFieldType } from "@/pages/settings/types";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { useUserFilterValues } from "@/entities/users/useUserSelect";
import { useUsersAll } from "@/entities/users/hooks";
import UserAvatar from "@/components/controls/UserAvatar";
import { useProducts, useCurrencies } from "@/entities/settings/hooks";
import { useBooleanAccountSetting } from "@/entities/settings/useBooleanAccountSetting";
import { Product } from "@/pages/settings/components/settingsItems/hooks/types";
import { isEmpty, mapKeys, uniqBy } from "lodash";
import toast from "react-hot-toast";
import { useArrMrr } from "@/services/context/ArrMrrContext";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { buildDeltaColumns } from "@/entities/dataTables/compareTo/deltaColumns";
import {
  deltaColumnId,
  deltaImpactColumnId,
  type CompareTranslationKey
} from "@/entities/dataTables/compareTo/columnIds";
import { buildCompareExport } from "@/entities/dataTables/compareTo/compareExport";
import { formatTimeframeLabel } from "@/entities/dataTables/compareTo/periods";
import { earliestKnownStart } from "@/entities/dataTables/compareTo/impactResolution";
import { useContractsComparison } from "@/entities/dataTables/compareTo/useContractsComparison";
import {
  DEFAULT_COMPARE_PERIOD,
  type CompareMetricKey,
  type CompareToSelection
} from "@/entities/dataTables/compareTo/types";
import TableNameCell from "@/components/controls/TableNameCell";
import NewQuickView from "@/components/layouts/NewQuickView/NewQuickView";
import { useNewQuickViewDrawer } from "@/services/hooks/entities/useNewQuickViewDrawer";
import { camelizeFieldKey } from "@/utils/fieldKeys";

const TABLE_ID = "contracts-table";

const CONTRACT_EXPORT_COLUMN_ID_MAP: Readonly<Record<string, string>> = {
  [ContractColumn.DURATION]: "durationDisplay"
};

// FK/display-name variants that are already covered by buildBaseFilterOptions.
// Exclude these from customFieldsForFilters to prevent duplicate filter entries.
// The terms half of each split money pair. Off by default for an account that
// has never signed in another currency — see useTermsColumnsVisibleByDefault.
const TERMS_COLUMN_IDS = new Set<string>([
  ContractColumn.TCV_TERMS,
  ContractColumn.ACV_TERMS,
  ContractColumn.ARR_TERMS,
  ContractColumn.MRR_TERMS,
  ContractColumn.CARR_TERMS,
  ContractColumn.CMRR_TERMS
]);

// The contract's FX rates. Always hidden by default: most accounts sign in
// one currency, and those that don't still read the converted figures first.
const FX_RATE_COLUMN_IDS = new Set<string>([
  ContractColumn.CLOSE_FX_RATE,
  ContractColumn.CURRENT_FX_RATE
]);

// Excel export: each terms column is priced in the row's own contract currency.
const EXPORT_CURRENCY_FIELD_BY_COLUMN: Readonly<Record<string, string>> =
  Object.fromEntries([...TERMS_COLUMN_IDS].map((id) => [id, "currency"]));

// The money metrics that get a Δ pair, in the order they appear in the table.
// `metricKey` doubles as the compare response's key and the row's own property
// name; `terms` is the same figure as signed, in the contract's own currency.
interface CompareMetricSlot {
  columnId: string;
  metricKey: CompareMetricKey;
  termsColumnId: string;
  termsMetricKey: CompareMetricKey;
}

// Derived from DataTable's own props so the conditional half below stays typed.
type SubtotalAggregation = NonNullable<
  NonNullable<
    ComponentProps<typeof DataTable>["groupingConfig"]
  >["subtotalAggregations"]
>[number];

// Status groups follow the order its enum declares, not whichever column is sorted.
const GROUP_RANK_RESOLVERS: GroupRankResolvers<ContractTableRow> = {
  [ContractColumn.STATUS]: statusRank
};

const getCompareMetricSlots = (isArr: boolean): CompareMetricSlot[] => [
  {
    columnId: ContractColumn.TCV,
    metricKey: "totalContractValue",
    termsColumnId: ContractColumn.TCV_TERMS,
    termsMetricKey: "totalContractValueLocal"
  },
  {
    columnId: ContractColumn.ACV,
    metricKey: "annualContractValue",
    termsColumnId: ContractColumn.ACV_TERMS,
    termsMetricKey: "annualContractValueLocal"
  },
  // The table shows one of the ARR/MRR pair at a time, so its Δ follows the
  // same toggle — a Δ MRR beside an ARR column would compare two different
  // things under one heading.
  {
    columnId: isArr ? ContractColumn.ARR : ContractColumn.MRR,
    metricKey: isArr ? "arr" : "mrr",
    termsColumnId: isArr ? ContractColumn.ARR_TERMS : ContractColumn.MRR_TERMS,
    termsMetricKey: isArr ? "arrLocal" : "mrrLocal"
  },
  {
    columnId: isArr ? ContractColumn.CARR : ContractColumn.CMRR,
    metricKey: isArr ? "carr" : "cmrr",
    termsColumnId: isArr
      ? ContractColumn.CARR_TERMS
      : ContractColumn.CMRR_TERMS,
    termsMetricKey: isArr ? "carrLocal" : "cmrrLocal"
  }
];

// The Δ, Impact and terms Δ column ids for one basis. The exportable set and
// the export's full exclusion set both come from here, so they cannot drift.
const getCompareColumnIds = (isArr: boolean): string[] =>
  getCompareMetricSlots(isArr).flatMap((slot) => [
    deltaColumnId(slot.columnId),
    deltaImpactColumnId(slot.columnId),
    deltaColumnId(slot.termsColumnId)
  ]);

// Both bases' ids: a saved view can hold the inactive basis's columns.
const ALL_COMPARE_COLUMN_IDS = [
  ...new Set([...getCompareColumnIds(true), ...getCompareColumnIds(false)])
];

// The selection the table starts on and that Display's Reset returns to. One
// constant so the two cannot drift apart.
const DEFAULT_COMPARE_TO: CompareToSelection = {
  period: DEFAULT_COMPARE_PERIOD
};

// Shared by the grid's Impact cells and the export's Impact labels, so the file
// reads exactly as the table does.
const CONTRACT_COMPARE_TRANSLATION_KEY: CompareTranslationKey =
  "contracts.compareTo";

/**
 * The real column the API groups its aggregations by for each group option the
 * toolbar offers. Anything absent keeps the client-side subtotal over the rows
 * loaded so far.
 */
const AGGREGATION_FIELD_BY_GROUP_FIELD: Record<string, string | undefined> = {
  [ContractColumn.STATUS]: ContractColumn.STATUS,
  [ContractColumn.SOURCE_DEAL]: "dealId"
};

const HARDCODED_FILTER_KEYS = new Set([
  ...Object.values(ContractColumn),
  "companyId",
  "productId",
  "statusName",
  "csmOwnerName",
  "aeOwnerName",
  // source_deal (fieldKey) → camelCase "sourceDeal" duplicates ContractColumn.SOURCE_DEAL ("dealName")
  "sourceDeal",
  // onboarding_stage_id (fieldKey) → camelCase "onboardingStageId" is a text
  // filter over the raw FK id. ContractColumn.ONBOARDING_STAGE below filters the
  // derived value the column actually displays.
  "onboardingStageId"
]);

// Registry fields whose camelCase key differs from the hardcoded column accessor they shadow.
// These are excluded from the column picker to prevent duplicate or unresolvable entries.
// E.g. source_deal → "sourceDeal" is already rendered as ContractColumn.SOURCE_DEAL ("dealName").
const COLUMN_PICKER_ALIAS_KEYS = new Set([
  "sourceDeal",
  "statusName",
  "csmOwnerName",
  "aeOwnerName"
]);

// Columns whose id is not a field the API can sort on. Period renders a
// start–end range, so it sorts by the range's start.
const SORT_FIELD_BY_COLUMN: Record<string, string> = {
  [ContractColumn.PERIOD]: "startDate"
};

const getSortByOptions = (isArr: boolean, fxRatesEnabled: boolean) => [
  ContractColumn.NAME,
  ContractColumn.COMPANY_NAME,
  ContractColumn.PRODUCT_NAME,
  ContractColumn.STATUS,
  ContractColumn.NEXT_RENEWAL,
  ContractColumn.NEXT_OPT_OUT,
  ContractColumn.TCV,
  isArr ? ContractColumn.ARR : ContractColumn.MRR,
  ContractColumn.SIGNED_ON,
  // The column ids are the API's own field names, so the sort needs no entry
  // in SORT_FIELD_BY_COLUMN.
  ...(fxRatesEnabled
    ? [ContractColumn.CLOSE_FX_RATE, ContractColumn.CURRENT_FX_RATE]
    : [])
];

const formatDate = (value: string | null) =>
  value ? formatFullDate(value) : "";

const formatPeriod = (
  start: string | null,
  end: string | null,
  ongoingLabel?: string
) => {
  if (!start && !end) return ongoingLabel ?? "";
  const startStr = start ? formatFullDate(start) : "";
  const endStr = ongoingLabel ?? (end ? formatFullDate(end) : "");
  return `${startStr} - ${endStr}`;
};

// The onboarding stage column displays a value derived from three fields, not
// the stage FK it stores. The API filters on the same derivation, whose
// non-stage branches are English literals in SQL
// (Contracts.get_computed_field_expressions in accountsession), so these are
// filter values and must stay untranslated — only their labels are localized.
const ONBOARDING_STAGE_NOT_STARTED_VALUE = "Not started";
const ONBOARDING_STAGE_COMPLETED_VALUE = "Completed";

interface BaseFilterOptionsArgs {
  products: Product[];
  userFilterValues: { value: string; label: string }[];
  currencies: string[];
  isArr: boolean;
  onboardingEnabled: boolean;
  onboardingStageValues: { value: string; label: string }[];
  fxRatesEnabled: boolean;
}

const buildBaseFilterOptions = ({
  products,
  userFilterValues,
  currencies,
  isArr,
  onboardingEnabled,
  onboardingStageValues,
  fxRatesEnabled
}: BaseFilterOptionsArgs): DataTableFilter[] => {
  return [
    { key: ContractColumn.NAME, filterType: { type: "text" } },
    {
      key: "companyId",
      filterType: {
        type: "map",
        values: [],
        sort: true,
        showSearch: true,
        entity: EntityType.COMPANY
      }
    },
    {
      key: "productId",
      filterType: {
        type: "map",
        values: products.map((product) => ({
          value: product.id,
          label: product.name
        })),
        sort: true,
        showSearch: true
      }
    },
    {
      key: ContractColumn.STATUS,
      excludeNoValue: true,
      filterType: {
        type: "enum",
        values: {
          enumType: ContractTableStatus,
          entityType: "contracts.table",
          valueType: "statusValues",
          // The table never lists drafts, so offering them would only ever
          // select an empty set.
          excludeValues: [ContractTableStatus.DRAFT],
          getIcon: (status: ContractTableStatus) =>
            status === ContractTableStatus.NO_SERVICES ||
            status === ContractTableStatus.ONE_TIME ? null : (
              <IconContainer
                size="xs"
                style={{ color: contractTableStatusColors[status] }}
              >
                {getContractStatusIcon(status)}
              </IconContainer>
            ),
          getLabelVariant: (status: ContractTableStatus) =>
            status === ContractTableStatus.NO_SERVICES ||
            status === ContractTableStatus.ONE_TIME
              ? "bodyTertiary"
              : undefined
        }
      }
    },
    {
      key: ContractColumn.RENEWAL_TYPE,
      filterType: {
        type: "enum",
        values: {
          enumType: ContractRenewalType,
          entityType: "contracts.table",
          valueType: "renewalTypeValues"
        }
      }
    },
    {
      key: ContractColumn.CSM_OWNER,
      filterType: {
        type: "map",
        values: userFilterValues,
        showAvatar: true,
        sort: true,
        showSearch: true,
        entity: EntityType.USER
      }
    },
    {
      key: ContractColumn.AE_OWNER,
      filterType: {
        type: "map",
        values: userFilterValues,
        showAvatar: true,
        sort: true,
        showSearch: true,
        entity: EntityType.USER
      }
    },
    {
      key: ContractColumn.CURRENCY,
      filterType: {
        type: "map",
        values: currencies.map((code) => ({ value: code, label: code })),
        sort: true,
        showSearch: true
      }
    },
    // A rate is a ratio between two currencies, not an amount in either, so
    // the filter is plain numeric rather than monetary.
    ...(fxRatesEnabled
      ? ([
          {
            key: ContractColumn.CLOSE_FX_RATE,
            filterType: { type: "numeric" }
          },
          {
            key: ContractColumn.CURRENT_FX_RATE,
            filterType: { type: "numeric" }
          }
        ] as DataTableFilter[])
      : []),
    {
      key: ContractColumn.TCV,
      filterType: { type: "numeric", isMonetary: true }
    },
    {
      key: ContractColumn.ACV,
      filterType: { type: "numeric", isMonetary: true }
    },
    {
      key: isArr ? ContractColumn.ARR : ContractColumn.MRR,
      filterType: { type: "numeric", isMonetary: true }
    },
    {
      key: isArr ? ContractColumn.CARR : ContractColumn.CMRR,
      filterType: { type: "numeric", isMonetary: true }
    },
    { key: ContractColumn.SERVICE_COUNT, filterType: { type: "numeric" } },
    { key: ContractColumn.DURATION, filterType: { type: "numeric" } },
    { key: ContractColumn.NEXT_RENEWAL, filterType: { type: "date" } },
    { key: ContractColumn.NEXT_OPT_OUT, filterType: { type: "date" } },
    { key: ContractColumn.SIGNED_ON, filterType: { type: "date" } },
    { key: ContractColumn.SOURCE_DEAL, filterType: { type: "text" } },
    ...(onboardingEnabled
      ? ([
          {
            key: ContractColumn.ONBOARDING_STAGE,
            // Every contract derives one of the offered values, so "(No value)"
            // would only ever match a dangling stage FK.
            excludeNoValue: true,
            filterType: {
              type: "map",
              values: onboardingStageValues,
              showSearch: true
            }
          }
        ] as DataTableFilter[])
      : []),
    {
      key: ContractColumn.CSM_RENEWAL_CONFIDENCE,
      filterType: {
        type: "enum",
        values: {
          enumType: CsmRenewalConfidenceLevel,
          entityType: "deals",
          valueType: "levels"
        }
      }
    }
  ];
};

interface Props {
  onExportStateChange?: ExportStateChangeHandler;
}

const ContractsTable: FC<Props> = ({ onExportStateChange }) => {
  const { t } = useTranslation();
  const {
    isArr,
    isLoading: arrMrrLoading,
    isResolved: arrMrrResolved,
    fieldName: arrOrMrrField,
    label: arrMrrLabel
  } = useArrMrr();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [filters, setFilters] = useState<Filters>(EmptyFilters);
  const [searchTerm, setSearchTerm] = useState("");
  const [groupBy, setGroupBy] = useState<string[]>([]);
  const [columnsState, setColumnsState] = useState<ColumnData[]>([]);
  const [selectedView, setSelectedView] = useState<ViewDataConfiguration>(
    EmptyView(ViewType.CONTRACTS_TABLE)
  );
  const [compareTo, setCompareTo] =
    useState<CompareToSelection>(DEFAULT_COMPARE_TO);
  const reconciledForViewRef = useRef<string | null>(null);

  const sortField = resolveSortField(sorting, SORT_FIELD_BY_COLUMN);
  const sortAsc = sorting[0] ? !sorting[0].desc : undefined;

  const {
    drawerState,
    openDrawer: openQuickView,
    closeDrawer: closeQuickView
  } = useNewQuickViewDrawer();
  // Export hook is defined below useFilteredContracts (line ~310) so it can
  // read total for adaptive poll cadence. State-change effect lives there too.

  const onRowClick = useCallback(
    (contract: ContractTableRow) => {
      openQuickView(contract.id, EntityType.CONTRACT);
    },
    [openQuickView]
  );

  // `undefined` until the setting loads, so the onboarding column and filter
  // never show and then disappear (reflowing the table) when it is off.
  const onboardingEnabled =
    useBooleanAccountSetting(ENABLE_ONBOARDING_PHASE_KEY).value === true;

  const { data: onboardingStagesData } = useOnboardingStages();

  // The vocabulary OnboardingStageCell renders, in the progression order the
  // API sorts by: not started, the configured stages, completed. Stage names
  // are not unique and the filter compares the displayed name, so same-named
  // stages collapse into the one option they are indistinguishable as.
  const onboardingStageValues = useMemo(
    () =>
      uniqBy(
        [
          {
            value: ONBOARDING_STAGE_NOT_STARTED_VALUE,
            label: t("contracts.table.onboardingStage.notStarted")
          },
          ...[...(onboardingStagesData?.stages ?? [])]
            .sort(
              (first, second) =>
                (first.itemOrder ?? 0) - (second.itemOrder ?? 0)
            )
            .map((stage) => ({ value: stage.name, label: stage.name })),
          {
            value: ONBOARDING_STAGE_COMPLETED_VALUE,
            label: t("contracts.table.onboardingStage.completed")
          }
        ],
        "value"
      ),
    [onboardingStagesData?.stages, t]
  );

  const { data: productsData } = useProducts();
  const { data: currenciesData } = useCurrencies();
  const { data: fieldsData } = useContractFields();
  const { accountCurrency, isResolved: accountCurrencyResolved } =
    useAccountCurrencyResolution();
  const {
    visibleByDefault: termsColumnsVisibleByDefault,
    isResolved: currencyUsageResolved
  } = useTermsColumnsVisibleByDefault();
  const { isFieldVisible, isLoaded: fieldsLoaded } = useFieldVisibility(
    EntityType.CONTRACT
  );
  const products = useMemo(
    () => productsData?.products ?? [],
    [productsData?.products]
  );
  const currencies = useMemo(() => currenciesData ?? [], [currenciesData]);
  const userFilterValues = useUserFilterValues();

  // A flag read starts false and resolves async, so the Δ columns are withheld
  // until it has actually answered. Branching on `enabled` alone would render
  // the flag-off table first and remount it a tick later.
  //
  // Read before the table's own fetch, which now carries the selected period so
  // the totals bar's as-of figure comes back with the aggregations beside it.
  const { enabled: isCompareToFlagOn, loading: compareToFlagLoading } =
    useFeatureFlag(FeatureFlags.CRM_9208_CONTRACT_AMENDMENTS);
  const compareToEnabled = isCompareToFlagOn && !compareToFlagLoading;
  const { enabled: fxRatesEnabled } = useFeatureFlag(
    FeatureFlags.CRM_11075_CONTRACT_FX_RATES
  );

  const {
    contracts,
    aggregations,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isLoading,
    isAggregationsLoading,
    isAggregationsError,
    isError,
    refetch,
    total,
    effectiveFilters,
    rawAggregations
  } = useFilteredContracts(
    sortField,
    sortAsc,
    filters,
    searchTerm,
    true,
    false,
    // The real column behind the group option, which is what the server can
    // group its aggregations by. Source deal shows the deal's name and is
    // stored as its id; custom columns stay on the client-side subtotal.
    AGGREGATION_FIELD_BY_GROUP_FIELD[groupBy[0]],
    compareToEnabled ? compareTo : undefined
  );

  const contractIds = useMemo(
    () => contracts.map((contract) => contract.id),
    [contracts]
  );

  const {
    byRowId: comparisonsByContractId,
    periodStart: comparisonPeriodStart,
    isError: isComparisonError
  } = useContractsComparison(contractIds, compareTo, compareToEnabled);

  // Without this the Δ cells are blank on failure, which is exactly how "this
  // contract has no prior record" renders — a failed request would read as a
  // book that did not move.
  useEffect(() => {
    if (!isComparisonError) return;
    toast.error(t("contracts.compareTo.loadFailed"));
  }, [isComparisonError, t]);

  const compareTimeframeLabel = useMemo(
    () => formatTimeframeLabel(compareTo, t),
    [compareTo, t]
  );

  // Every Δ and Impact column id the feature can produce. Deliberately the
  // full set for the active basis rather than only the columns that survived
  // the field-visibility gate: the consumers below only ever match these against
  // columns that exist (the Sort by exclusions, the export's exportable set),
  // where naming a column that is not there is a no-op, and a superset cannot
  // fall behind the gate. Empty while the flag is closed, which keeps every
  // consumer as it was for anyone without the feature.
  const compareToColumnIds = useMemo(
    () => (compareToEnabled ? getCompareColumnIds(isArr) : []),
    [compareToEnabled, isArr]
  );

  // The export computes the Δ and Impact columns server-side for the period
  // sent in its filter, so the active basis's columns export while the flag is
  // open. Every other id the feature can generate stays excluded: the inactive
  // basis lingers in a saved view (the reconciliation effect only relabels, and
  // mergeMissingDefaultColumns only adds), and so does every Δ id once the flag
  // closes. Enumerated rather than matched on the `Delta` suffix — a visible
  // custom field named "Price Delta" becomes `priceDelta`, and a suffix test
  // would take a legitimate column out of the file.
  const compareExport = useMemo(
    () =>
      buildCompareExport({
        enabled: compareToEnabled,
        selection: compareTo,
        allColumnIds: ALL_COMPARE_COLUMN_IDS,
        exportableColumnIds: compareToColumnIds,
        translationKey: CONTRACT_COMPARE_TRANSLATION_KEY,
        t
      }),
    [compareToEnabled, compareTo, compareToColumnIds, t]
  );

  // Sorting is handed to the API, which has no field to order these by, so they
  // are withheld from the Sort by list rather than offered and then rejected.
  const compareToSortMenuExclusions = useMemo(
    () => new Set(compareToColumnIds),
    [compareToColumnIds]
  );

  const { exportTo: exportContracts, isExporting: isExportingContracts } =
    useEntityExport({
      createRoute: "contracts/export/create",
      statusRoute: "contracts/export/status",
      tableName: "contracts",
      filters: effectiveFilters,
      sorting,
      searchTerm,
      columnsState,
      totalRows: total,
      getFilterOptions: () => filterOptions,
      excludeFromExport: compareExport.excludedColumnIds,
      exportColumnIdMap: CONTRACT_EXPORT_COLUMN_ID_MAP,
      sortFieldByColumn: SORT_FIELD_BY_COLUMN,
      columnCurrencyFields: EXPORT_CURRENCY_FIELD_BY_COLUMN,
      getExtraColumnValueLabels: () => compareExport.impactValueLabels,
      getExtraFilterParams: compareExport.getFilterParams,
      // Read at export time, so `columnIds` (declared below) is initialised.
      getColumnLabelOverrides: () => compareExport.pickColumnLabels(columnIds)
    });

  useNotifyExportState(
    onExportStateChange,
    exportContracts,
    isExportingContracts
  );

  const handleLoadMore = useCallback(() => {
    if (!isFetchingNextPage && hasNextPage && !isError) fetchNextPage();
  }, [isFetchingNextPage, hasNextPage, isError, fetchNextPage]);

  const handleClearFilters = useCallback(() => setFilters(EmptyFilters), []);

  const { users: allUsers } = useUsersAll();

  const userById = useMemo(() => {
    const map = new Map<string, (typeof allUsers)[number]>();
    allUsers.forEach((user) => {
      if (user?.id) map.set(user.id, user);
    });
    return map;
  }, [allUsers]);

  useEffect(() => {
    const tempView = getTempView(selectedView);
    if (tempView?.config?.filters) {
      setFilters(tempView.config.filters);
    }
    if (tempView?.config?.searchTerm) {
      setSearchTerm(tempView.config.searchTerm);
    }
    if (tempView?.config?.groupBy) {
      setGroupBy(tempView.config.groupBy);
    }
  }, [selectedView]);

  useEffect(() => {
    setSorting((prev) =>
      prev.map((sortEntry) => {
        if (
          isArr &&
          (sortEntry.id === ContractColumn.MRR ||
            sortEntry.id === ContractColumn.CMRR)
        ) {
          return {
            ...sortEntry,
            id:
              sortEntry.id === ContractColumn.MRR
                ? ContractColumn.ARR
                : ContractColumn.CARR
          };
        }
        if (
          !isArr &&
          (sortEntry.id === ContractColumn.ARR ||
            sortEntry.id === ContractColumn.CARR)
        ) {
          return {
            ...sortEntry,
            id:
              sortEntry.id === ContractColumn.ARR
                ? ContractColumn.MRR
                : ContractColumn.CMRR
          };
        }
        return sortEntry;
      })
    );
  }, [isArr]);

  const selectedColumns = useMemo(() => {
    if (columnsState.length > 0) {
      return columnsState;
    }
    const tempView = getTempView(selectedView);
    return tempView?.config?.columns || selectedView?.config?.columns || [];
  }, [columnsState, selectedView]);

  // Hoisted out of the columns memo so the totals bar's Δ tile can be handed the
  // very string the Δ column headers are built from. The bar has an ARR/MRR
  // label of its own (`useArrMrr`, from `terms.arr`); using it there would make
  // the two agree only for as long as `terms.arr` and `contracts.arr` happen to
  // hold the same word.
  const arrOrMrrLabel = useMemo(
    () =>
      isArr
        ? t(`contracts.${ContractColumn.ARR}`)
        : t(`contracts.${ContractColumn.MRR}`),
    [isArr, t]
  );

  const columns = useMemo<ColumnDef<ContractTableRow, unknown>[]>(() => {
    const carrOrCmrrLabel = isArr
      ? t(`contracts.${ContractColumn.CARR}`)
      : t(`contracts.${ContractColumn.CMRR}`);

    // Δ columns exist only while the flag is open, so nothing downstream —
    // columnIds, defaultColumnsState, the saved-view merge — can see them
    // otherwise. Placed next to the metric they describe, per the design.
    const deltaColumnsFor = (
      columnId: string,
      metricKey: CompareMetricKey,
      metricLabel: string,
      isTermsCurrency: boolean
    ): ColumnDef<ContractTableRow, unknown>[] =>
      compareToEnabled
        ? buildDeltaColumns<ContractTableRow>({
            metricKey,
            columnId,
            metricLabel,
            timeframe: compareTimeframeLabel,
            periodStart: comparisonPeriodStart,
            // The endpoint always measures against live values, so the window
            // closes today. Read inside the memo rather than as a dependency:
            // a fresh Date every render would rebuild every column.
            periodEnd: toDateOnlyISO(new Date()),
            byRowId: comparisonsByContractId,
            isTermsCurrency,
            translationKey: CONTRACT_COMPARE_TRANSLATION_KEY,
            // One Impact column per metric, on the account-currency half.
            includeImpact: !isTermsCurrency,
            // Contracts is one of the two grains that can show New: the
            // row itself carries the dates that establish it.
            allowNew: true,
            readRowStart: (row) =>
              earliestKnownStart(row.startDate, row.signedOn),
            t
          })
        : [];

    const slotFor = (columnId: string) =>
      getCompareMetricSlots(isArr).find(
        (slot) => slot.columnId === columnId || slot.termsColumnId === columnId
      );

    // A Δ column carries the same figures as the metric it describes, so it
    // has to answer to the same field-visibility gate. It cannot rely on the
    // filter below to do that: `isFieldVisible` passes any key the entity's
    // field registry does not know, and a synthetic `arrDelta` is never a
    // registry key — so a tenant that hid ARR would still be shown Δ ARR.
    const isMetricVisible = (columnId: string) =>
      !fieldsLoaded || isFieldVisible(columnId);

    const systemDeltas = (columnId: string, metricLabel: string) => {
      const slot = slotFor(columnId);
      if (!slot || !isMetricVisible(slot.columnId)) return [];
      return deltaColumnsFor(slot.columnId, slot.metricKey, metricLabel, false);
    };

    const termsDeltas = (columnId: string, metricLabel: string) => {
      const slot = slotFor(columnId);
      if (!slot || !isMetricVisible(slot.termsColumnId)) return [];
      return deltaColumnsFor(
        slot.termsColumnId,
        slot.termsMetricKey,
        termsCurrencyHeader(metricLabel, t),
        true
      );
    };

    const columnDefs: ColumnDef<ContractTableRow, unknown>[] = [
      {
        header: t(`contracts.${ContractColumn.NAME}`),
        accessorKey: ContractColumn.NAME,
        cell: (info) => (
          <TableNameCell info={info} entityType={EntityType.CONTRACT} />
        )
      },
      {
        header: t(`contracts.${ContractColumn.COMPANY_NAME}`),
        accessorKey: ContractColumn.COMPANY_NAME,
        meta: { filterKey: "companyId" },
        cell: (info) => info.row.original.companyName || ""
      },
      {
        header: t(`contracts.${ContractColumn.PRODUCT_NAME}`),
        accessorKey: ContractColumn.PRODUCT_NAME,
        meta: { filterKey: "productId" },
        cell: (info) => info.row.original.productName || ""
      },
      {
        id: ContractColumn.STATUS,
        header: t(`contracts.${ContractColumn.STATUS}`),
        accessorFn: getContractStatusValue,
        meta: { filterKey: ContractColumn.STATUS },
        size: 190,
        cell: (info) => {
          const status = info.row.original.status;
          if (status == null) return "";
          return (
            <ContractStatusBadge
              status={status}
              displayFallback={info.row.original.statusResolved}
              isTable
            />
          );
        }
      },
      {
        id: ContractColumn.RENEWAL_TYPE,
        header: t(`contracts.${ContractColumn.RENEWAL_TYPE}`),
        accessorKey: "renewalTypeResolved",
        meta: { filterKey: ContractColumn.RENEWAL_TYPE },
        cell: (info) => {
          const renewalType = info.row.original.renewalType;
          if (renewalType == null) return "";
          return tWithFallback(
            getEnumTKey(
              "contracts.table",
              "renewalTypeValues",
              ContractRenewalType,
              renewalType
            ),
            info.row.original.renewalTypeResolved
          );
        }
      },
      {
        header: t(`contracts.${ContractColumn.NEXT_RENEWAL}`),
        accessorKey: ContractColumn.NEXT_RENEWAL,
        meta: { groupByMonth: true },
        cell: (info) => formatDate(info.row.original.nextRenewal)
      },
      {
        header: t(`contracts.${ContractColumn.NEXT_OPT_OUT}`),
        accessorKey: ContractColumn.NEXT_OPT_OUT,
        meta: { groupByMonth: true },
        cell: (info) => formatDate(info.row.original.nextOptOut)
      },
      {
        header: systemCurrencyHeader(
          t(`contracts.${ContractColumn.TCV}`),
          accountCurrency
        ),
        accessorKey: ContractColumn.TCV,
        size: 160,
        cell: (info) => (
          <SystemCurrencyCell amount={info.row.original.totalContractValue} />
        )
      },
      ...systemDeltas(ContractColumn.TCV, t(`contracts.${ContractColumn.TCV}`)),
      {
        header: termsCurrencyHeader(t(`contracts.${ContractColumn.TCV}`), t),
        accessorKey: ContractColumn.TCV_TERMS,
        // Each row is denominated in its own contract currency, so ordering
        // the column compares unlike units. The system half beside it sorts.
        enableSorting: false,
        size: 160,
        cell: (info) => (
          <TermsCurrencyCell
            amount={info.row.original.totalContractValueLocal}
            currency={info.row.original.currency}
          />
        )
      },
      ...termsDeltas(
        ContractColumn.TCV_TERMS,
        t(`contracts.${ContractColumn.TCV}`)
      ),
      {
        header: systemCurrencyHeader(
          t(`contracts.${ContractColumn.ACV}`),
          accountCurrency
        ),
        accessorKey: ContractColumn.ACV,
        size: 160,
        cell: (info) => (
          <SystemCurrencyCell amount={info.row.original.annualContractValue} />
        )
      },
      ...systemDeltas(ContractColumn.ACV, t(`contracts.${ContractColumn.ACV}`)),
      {
        header: termsCurrencyHeader(t(`contracts.${ContractColumn.ACV}`), t),
        accessorKey: ContractColumn.ACV_TERMS,
        // Each row is denominated in its own contract currency, so ordering
        // the column compares unlike units. The system half beside it sorts.
        enableSorting: false,
        size: 160,
        cell: (info) => (
          <TermsCurrencyCell
            amount={info.row.original.annualContractValueLocal}
            currency={info.row.original.currency}
          />
        )
      },
      ...termsDeltas(
        ContractColumn.ACV_TERMS,
        t(`contracts.${ContractColumn.ACV}`)
      ),
      {
        id: isArr ? ContractColumn.ARR : ContractColumn.MRR,
        header: systemCurrencyHeader(arrOrMrrLabel, accountCurrency),
        accessorFn: (row) => (isArr ? row.arr : row.mrr),
        size: 160,
        cell: (info) => (
          <SystemCurrencyCell
            amount={isArr ? info.row.original.arr : info.row.original.mrr}
          />
        )
      },
      ...systemDeltas(
        isArr ? ContractColumn.ARR : ContractColumn.MRR,
        arrOrMrrLabel
      ),
      {
        id: isArr ? ContractColumn.ARR_TERMS : ContractColumn.MRR_TERMS,
        // Each row is denominated in its own contract currency, so ordering
        // the column compares unlike units. The system half beside it sorts.
        enableSorting: false,
        header: termsCurrencyHeader(arrOrMrrLabel, t),
        accessorFn: (row) => (isArr ? row.arrLocal : row.mrrLocal),
        size: 160,
        cell: (info) => (
          <TermsCurrencyCell
            amount={
              isArr ? info.row.original.arrLocal : info.row.original.mrrLocal
            }
            currency={info.row.original.currency}
          />
        )
      },
      ...termsDeltas(
        isArr ? ContractColumn.ARR_TERMS : ContractColumn.MRR_TERMS,
        arrOrMrrLabel
      ),
      {
        id: isArr ? ContractColumn.CARR : ContractColumn.CMRR,
        header: systemCurrencyHeader(carrOrCmrrLabel, accountCurrency),
        accessorFn: (row) => (isArr ? row.carr : row.cmrr),
        size: 160,
        cell: (info) => (
          <SystemCurrencyCell
            amount={isArr ? info.row.original.carr : info.row.original.cmrr}
          />
        )
      },
      ...systemDeltas(
        isArr ? ContractColumn.CARR : ContractColumn.CMRR,
        carrOrCmrrLabel
      ),
      {
        id: isArr ? ContractColumn.CARR_TERMS : ContractColumn.CMRR_TERMS,
        // Each row is denominated in its own contract currency, so ordering
        // the column compares unlike units. The system half beside it sorts.
        enableSorting: false,
        header: termsCurrencyHeader(carrOrCmrrLabel, t),
        accessorFn: (row) => (isArr ? row.carrLocal : row.cmrrLocal),
        size: 160,
        cell: (info) => (
          <TermsCurrencyCell
            amount={
              isArr ? info.row.original.carrLocal : info.row.original.cmrrLocal
            }
            currency={info.row.original.currency}
          />
        )
      },
      ...termsDeltas(
        isArr ? ContractColumn.CARR_TERMS : ContractColumn.CMRR_TERMS,
        carrOrCmrrLabel
      ),
      {
        header: t(`contracts.${ContractColumn.SERVICE_COUNT}`),
        accessorKey: ContractColumn.SERVICE_COUNT,
        meta: { textAlign: "center" },
        cell: (info) => info.row.original.serviceCount ?? "—"
      },
      {
        header: t(`contracts.${ContractColumn.PERIOD}`),
        accessorKey: ContractColumn.PERIOD,
        size: 220,
        cell: (info) => {
          const row = info.row.original;
          const isAutoRenewal = row.renewalType === ContractRenewalType.AUTO;
          return formatPeriod(
            row.startDate,
            row.endDate,
            isAutoRenewal ? t("contracts.detailedView.ongoing") : undefined
          );
        }
      },
      {
        header: t(`contracts.${ContractColumn.DURATION}`),
        accessorKey: ContractColumn.DURATION,
        meta: { textAlign: "center" },
        cell: (info) => {
          const row = info.row.original;
          if (row.renewalType === ContractRenewalType.AUTO) {
            return t("contracts.detailedView.ongoing");
          }
          return row.durationDisplay ?? "—";
        }
      },
      {
        header: t(`contracts.${ContractColumn.SIGNED_ON}`),
        accessorKey: ContractColumn.SIGNED_ON,
        meta: { groupByMonth: true },
        cell: (info) => formatDate(info.row.original.signedOn)
      },
      {
        header: t(`contracts.${ContractColumn.CURRENCY}`),
        accessorKey: ContractColumn.CURRENCY,
        cell: (info) => info.row.original.currency || ""
      },
      ...(fxRatesEnabled
        ? [
            {
              header: t(`contracts.${ContractColumn.CLOSE_FX_RATE}`),
              accessorKey: ContractColumn.CLOSE_FX_RATE,
              cell: (info: { row: { original: ContractTableRow } }) => (
                <FxRateCell
                  rate={info.row.original.closeFxRate}
                  contractCurrency={info.row.original.currency}
                  accountCurrency={accountCurrency}
                />
              )
            },
            {
              header: t(`contracts.${ContractColumn.CURRENT_FX_RATE}`),
              accessorKey: ContractColumn.CURRENT_FX_RATE,
              cell: (info: { row: { original: ContractTableRow } }) => (
                <FxRateCell
                  rate={info.row.original.currentFxRate}
                  contractCurrency={info.row.original.currency}
                  accountCurrency={accountCurrency}
                  isManual={info.row.original.currentFxRateModifiedBy != null}
                />
              )
            }
          ]
        : []),
      {
        header: t(`contracts.${ContractColumn.CSM_OWNER}`),
        accessorKey: ContractColumn.CSM_OWNER,
        cell: (info) => {
          const ownerId = info.row.original.csmOwner;
          if (!ownerId) return "";
          const user = userById.get(ownerId);
          const name =
            info.row.original.csmOwnerName || user?.displayName || "";
          return (
            <div className="flex items-center gap-2">
              <UserAvatar userData={user} size="M" />
              <span className="truncate">{name}</span>
            </div>
          );
        }
      },
      {
        header: t(`contracts.${ContractColumn.AE_OWNER}`),
        accessorKey: ContractColumn.AE_OWNER,
        cell: (info) => {
          const ownerId = info.row.original.aeOwner;
          if (!ownerId) return "";
          const user = userById.get(ownerId);
          const name = info.row.original.aeOwnerName || user?.displayName || "";
          return (
            <div className="flex items-center gap-2">
              <UserAvatar userData={user} size="M" />
              <span className="truncate">{name}</span>
            </div>
          );
        }
      },
      {
        header: t(`contracts.${ContractColumn.SOURCE_DEAL}`),
        accessorKey: ContractColumn.SOURCE_DEAL,
        cell: (info) => info.row.original.dealName || ""
      },
      ...(onboardingEnabled
        ? [
            {
              header: t(`contracts.${ContractColumn.ONBOARDING_STAGE}`),
              accessorKey: ContractColumn.ONBOARDING_STAGE,
              cell: (info: { row: { original: ContractTableRow } }) => (
                <OnboardingStageCell row={info.row.original} />
              )
            }
          ]
        : []),
      {
        header: t(`contracts.${ContractColumn.CSM_RENEWAL_CONFIDENCE}`),
        accessorKey: ContractColumn.CSM_RENEWAL_CONFIDENCE,
        cell: (info) => {
          const { csmRenewalConfidenceLevel } = info.row.original;
          if (!csmRenewalConfidenceLevel) return null;
          return (
            <div className="flex items-center gap-1">
              <ConfidenceLevelIcon value={Number(csmRenewalConfidenceLevel)} />
              <Text variant="body">
                {t(
                  getEnumTKey(
                    "deals",
                    "levels",
                    CsmRenewalConfidenceLevel,
                    Number(csmRenewalConfidenceLevel)
                  )
                )}
              </Text>
            </div>
          );
        }
      },
      ...mapSelectedCustomColumnsToTableColumns(
        selectedColumns,
        fieldsData?.fields,
        t,
        new Set(Object.values(ContractColumn)),
        groupBy
      )
    ];
    return !fieldsLoaded
      ? columnDefs
      : columnDefs.filter((col) => {
          // Prefer explicit `id` (stable column identity) over `accessorKey`
          // because some columns use a `*Display` accessor for CSV export but
          // keep the original field key as `id` for field-visibility lookups.
          const key = (col.id ??
            (col as { accessorKey?: string }).accessorKey) as string;
          return isFieldVisible(key);
        });
  }, [
    t,
    userById,
    isArr,
    accountCurrency,
    onboardingEnabled,
    selectedColumns,
    fieldsData?.fields,
    isFieldVisible,
    fieldsLoaded,
    groupBy,
    compareToEnabled,
    fxRatesEnabled,
    compareTimeframeLabel,
    arrOrMrrLabel,
    comparisonPeriodStart,
    comparisonsByContractId
  ]);

  const columnIds = useMemo<ColumnData[]>(
    () =>
      columns.map((column, order) => {
        const id = ((column.id as string) ??
          (column as { accessorKey?: string }).accessorKey) as string;
        return {
          id,
          // `header` is a render function on any column with a tooltip, and
          // `String()` on a function yields its source. Columns that carry a
          // plain label in meta expose it there for exactly this reason.
          label:
            (column.meta as { label?: string } | undefined)?.label ??
            String(column.header ?? ""),
          order,
          // Always true: this list is read for ids and labels only. The default
          // visibility of the terms halves is applied where it is actually
          // consumed, on `defaultColumnsState` below.
          isVisible: true
        };
      }),
    [columns]
  );

  const groupByOptions = useMemo(
    () => getVisibleContractsGroupByOptions(columnIds.map((col) => col.id)),
    [columnIds]
  );

  // One grouped response already carries every group and its own COUNT(*), so
  // a group header shows its true figures before its rows are paged in. Both
  // group options show something other than the stored value — the status
  // badge's text, the deal's name — so the summaries are re-keyed to match.
  const groupField = groupBy[0];
  const isServerGrouped = !!AGGREGATION_FIELD_BY_GROUP_FIELD[groupField];

  // The deals behind every group the response named, so a deal whose contracts
  // are still paging in heads a group by name rather than waiting for a page.
  const groupDealIds = useMemo(
    () =>
      groupField === ContractColumn.SOURCE_DEAL
        ? groupFieldValues(rawAggregations)
        : [],
    [groupField, rawAggregations]
  );

  const { entities: groupDeals } = useDeals(groupDealIds, {
    enabled: groupDealIds.length > 0
  });

  const dealNameById = useMemo(() => {
    const names = buildDisplayValueById(
      contracts,
      "dealId",
      ContractColumn.SOURCE_DEAL
    );
    groupDeals.forEach((deal) => {
      if (deal?.id && deal.name) names[deal.id] = deal.name;
    });
    return names;
  }, [contracts, groupDeals]);

  const groupSummaries = useMemo(() => {
    if (!isServerGrouped) return undefined;
    const byFieldValue = buildGroupSummaries(
      rawAggregations,
      [
        "totalContractValue",
        "annualContractValue",
        "arr",
        "mrr",
        "carr",
        "cmrr"
      ],
      isAggregationsLoading
    );
    if (groupField === ContractColumn.SOURCE_DEAL) {
      return rekeyGroupSummaries(
        byFieldValue,
        (dealId) => dealNameById[dealId] ?? dealId
      );
    }
    return mapKeys(byFieldValue, (_summary, fieldValue) =>
      fieldValue === ""
        ? ""
        : getContractStatusLabel(Number(fieldValue) as ContractTableStatus)
    );
  }, [
    isServerGrouped,
    groupField,
    dealNameById,
    rawAggregations,
    isAggregationsLoading
  ]);

  // Listed in status order, but only the statuses the filtered set actually
  // has — the list view hides drafts, and a permanently empty group is noise.
  // Deals are listed by the names the loaded rows resolved; an id no row has
  // reached yet names a group nobody could read, so it waits for its page.
  const knownGroups = useMemo(() => {
    if (!groupSummaries) return undefined;
    if (groupField === ContractColumn.SOURCE_DEAL) {
      const names = new Set(Object.values(dealNameById));
      return Object.keys(groupSummaries)
        .filter((key) => key === "" || names.has(key))
        .sort((a, b) => a.localeCompare(b))
        .map((name) => ({ value: name, label: name }));
    }
    return Object.values(ContractTableStatus)
      .filter(
        (value): value is ContractTableStatus => typeof value === "number"
      )
      .map((status) => getContractStatusLabel(status))
      .filter((label) => label in groupSummaries)
      .map((label) => ({ value: label, label }));
  }, [groupSummaries, groupField, dealNameById]);

  const filterOptions = useMemo(
    () =>
      buildBaseFilterOptions({
        products,
        userFilterValues,
        currencies,
        isArr,
        onboardingEnabled,
        onboardingStageValues,
        fxRatesEnabled
      }),
    [
      products,
      userFilterValues,
      currencies,
      isArr,
      onboardingEnabled,
      onboardingStageValues,
      fxRatesEnabled
    ]
  );

  const sortByOptions = useMemo(
    () => getSortByOptions(isArr, fxRatesEnabled),
    [isArr, fxRatesEnabled]
  );

  // A Δ column inherits its base metric's currency basis, so a terms Δ has to
  // start hidden wherever the terms column it describes does — otherwise an
  // account that has only ever signed in its own currency gets the Δ half of a
  // pair whose other half it was deliberately not shown.
  const termsHiddenByDefaultIds = useMemo(() => {
    const ids = new Set<string>(TERMS_COLUMN_IDS);
    if (compareToEnabled) {
      getCompareMetricSlots(isArr).forEach((slot) =>
        ids.add(deltaColumnId(slot.termsColumnId))
      );
    }
    return ids;
  }, [compareToEnabled, isArr]);

  // The FX columns start hidden on every account, unlike the terms columns,
  // which only start hidden where the account has never signed in another
  // currency.
  const hiddenByDefaultIds = useMemo(
    () =>
      new Set<string>([
        ...(termsColumnsVisibleByDefault ? [] : termsHiddenByDefaultIds),
        ...FX_RATE_COLUMN_IDS
      ]),
    [termsColumnsVisibleByDefault, termsHiddenByDefaultIds]
  );

  const defaultColumnsState = useMemo<ColumnData[]>(() => {
    const hardcodedIds = new Set(columnIds.map((c) => c.id));
    const pipelineFields = (fieldsData?.fields ?? []).filter(
      (field) => !hardcodedIds.has(camelizeFieldKey(field.fieldKey))
    );
    const state = getDefaultColumnsStateWithCustomFields(
      columnIds.map((c) => c.id),
      fieldsData?.fields?.filter(
        (field) =>
          !COLUMN_PICKER_ALIAS_KEYS.has(camelizeFieldKey(field.fieldKey))
      ),
      "contracts",
      t,
      undefined,
      undefined,
      hiddenByDefaultIds,
      // The split money columns compose their header at render time (currency
      // code or basis); the checkbox has to read the same or the two halves of a
      // pair are indistinguishable in Columns management.
      new Map(columnIds.map((column) => [column.id, column.label]))
    );
    const fieldMap = new Map(
      pipelineFields.map((f) => [camelizeFieldKey(f.fieldKey), f])
    );
    return state.map((col) => {
      if (col.isVisible) return col;
      // The terms-hidden-by-default gate above is meant to be the final word
      // on a terms column's visibility (see getDefaultColumnsStateWithCustomFields),
      // so a terms id must never be re-shown here regardless of its registry
      // field type. This also covers the inactive isArr half (e.g. mrrLocal
      // when isArr is true) — it isn't in hardcodedIds, so it reaches this
      // pipeline path same as the active half.
      if (termsHiddenByDefaultIds.has(col.id)) return col;
      if (FX_RATE_COLUMN_IDS.has(col.id)) return col;
      const field = fieldMap.get(col.id);
      if (!field) return col;
      if (
        field.fieldType === SettingsFieldType.SYSTEM ||
        field.fieldType === SettingsFieldType.FORMULA ||
        (field.fieldType === SettingsFieldType.LINKED &&
          field.showInDetailedView)
      ) {
        return { ...col, isVisible: true };
      }
      return col;
    });
  }, [
    columnIds,
    fieldsData?.fields,
    t,
    hiddenByDefaultIds,
    termsHiddenByDefaultIds
  ]);

  useEffect(() => {
    if (reconciledForViewRef.current === selectedView.id) return;
    const tempView = getTempView(selectedView);
    const persistedColumns =
      tempView?.config?.columns ?? selectedView?.config?.columns;
    if (persistedColumns && persistedColumns.length > 0) {
      // The relabel below reads accountCurrency off `columnIds` to refresh the
      // system-currency headers (`TCV (USD)`) — this write is a one-time
      // snapshot into columnsState, so an unresolved currency would freeze
      // the bare, no-currency label in it permanently, unlike the live grid
      // header which just re-renders once the setting loads.
      if (!accountCurrencyResolved) return;
      reconciledForViewRef.current = selectedView.id;
      // The split money columns compose their header at render time (currency
      // code or basis), so a label saved into the view can go stale — re-derive
      // it from the live column definition instead of trusting the persisted
      // string verbatim (that's how a CSV export ends up missing "(USD)").
      const currentLabelById = new Map(
        columnIds.map((column) => [column.id, column.label])
      );
      setColumnsState(
        persistedColumns.map((column: ColumnData) => ({
          ...column,
          label: currentLabelById.get(column.id) ?? column.label
        }))
      );
    }
  }, [selectedView, columnIds, accountCurrencyResolved]);

  // Opening a view saved without columns empties the layout, so the seeding
  // below re-runs on that too — under the same gates, never ahead of them.
  const isColumnsStateEmpty = columnsState.length === 0;
  useEffect(() => {
    // Currency usage has to have settled first: the terms columns default to
    // visible while it is in flight, and seeding that guess is permanent. Same
    // reasoning applies to account currency: the system-currency headers
    // (TCV, ACV, ARR/MRR, CARR/CMRR) are bare until it resolves.
    // Same for the ARR/MRR basis: MRR until resolved, unseeded if the read fails.
    if (
      !fieldsData ||
      !currencyUsageResolved ||
      !accountCurrencyResolved ||
      !arrMrrResolved
    )
      return;
    setColumnsState((prev) => (prev.length === 0 ? defaultColumnsState : prev));
  }, [
    defaultColumnsState,
    isColumnsStateEmpty,
    fieldsData,
    currencyUsageResolved,
    accountCurrencyResolved,
    arrMrrResolved
  ]);

  useEffect(() => {
    // A saved layout predates the terms columns, so seeding alone would never
    // offer them to an account that already has one. Gated on the same reads
    // as seeding, and for the same reasons: a terms column folded in while
    // currency usage is still in flight is folded in visible, and a system
    // currency column folded in before account currency resolves is folded in
    // with a bare, no-currency label — both merges are one-time and the guess
    // is saved with the layout.
    // Same for the ARR/MRR basis: the wrong half folded in stays for good.
    if (
      !fieldsData ||
      !currencyUsageResolved ||
      !accountCurrencyResolved ||
      !arrMrrResolved
    )
      return;
    setColumnsState((prev) =>
      mergeMissingDefaultColumns(prev, defaultColumnsState)
    );
  }, [
    defaultColumnsState,
    fieldsData,
    currencyUsageResolved,
    accountCurrencyResolved,
    arrMrrResolved
  ]);

  const customFieldsForFilters = useMemo(
    () =>
      (fieldsData?.fields ?? []).filter(
        (field) => !HARDCODED_FILTER_KEYS.has(camelizeFieldKey(field.fieldKey))
      ),
    [fieldsData?.fields]
  );

  return (
    <>
      <ContractsBottomKPIBar
        aggregations={aggregations}
        isLoading={isLoading || isAggregationsLoading}
        isError={isAggregationsError}
        compareTimeframeLabel={
          compareToEnabled ? compareTimeframeLabel : undefined
        }
        compareMetricLabel={compareToEnabled ? arrOrMrrLabel : undefined}
      />
      <DataTableToolbar
        viewType={ViewType.CONTRACTS_TABLE}
        selectedView={selectedView}
        setSelectedView={setSelectedView}
        filterOptions={filterOptions}
        filters={filters}
        setFilters={setFilters}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        groupBy={groupBy}
        setGroupBy={setGroupBy}
        sorting={sorting}
        onSortingChange={setSorting}
        defaultColumnsState={defaultColumnsState}
        columnsState={columnsState}
        setColumnsState={setColumnsState}
        sortByOptions={sortByOptions}
        defaultSortByValue={ContractColumn.NAME}
        tableId={TABLE_ID}
        customFields={customFieldsForFilters}
      />
      <ToolbarActions
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        filters={filters}
        setFilters={setFilters}
        filterOptions={filterOptions}
        groupBy={groupBy}
        setGroupBy={setGroupBy}
        groupByOptions={groupByOptions}
        sorting={sorting}
        onSortingChange={setSorting}
        sortByOptions={sortByOptions}
        defaultSortByValue={ContractColumn.NAME}
        columnsState={columnsState}
        setColumnsState={setColumnsState}
        defaultColumnsState={defaultColumnsState}
        selectedView={selectedView}
        setSelectedView={setSelectedView}
        viewType={ViewType.CONTRACTS_TABLE}
        customFields={customFieldsForFilters}
        groupByFields={fieldsData?.fields}
        tableId={TABLE_ID}
        className="px-4 py-2"
        compareTo={compareToEnabled ? compareTo : undefined}
        onCompareToChange={compareToEnabled ? setCompareTo : undefined}
        defaultCompareTo={DEFAULT_COMPARE_TO}
        sortMenuExcludedColumnIds={compareToSortMenuExclusions}
      />
      <DataTable
        groupRankResolvers={GROUP_RANK_RESOLVERS}
        key={TABLE_ID}
        dataTest={TABLE_ID}
        tableId={TABLE_ID}
        columns={columns as ColumnDef<unknown, unknown>[]}
        data={contracts}
        manualSorting
        onSortingChange={setSorting}
        initialSortBy={sorting}
        isInfiniteScroll
        hasMore={hasNextPage}
        isDataLoading={isLoading}
        isLoadingMore={isFetchingNextPage}
        onLoadMore={handleLoadMore}
        loadingThreshold={100}
        resizableColumns
        enableColumnReorder
        stickyLeftColumn
        headerClassName="border-none"
        className="h-full"
        filterOptions={filterOptions}
        filters={filters}
        setFilters={setFilters}
        hasActiveFilters={!isEmpty(filters)}
        onClearFilters={handleClearFilters}
        onClearSearch={() => setSearchTerm("")}
        isError={isError}
        onRetry={refetch}
        onColumnsChange={setColumnsState}
        groupBy={groupBy}
        groupingConfig={{
          typeLabel: t("contracts.bottomKPI.total"),
          aggregationKey: arrMrrLoading ? undefined : arrOrMrrField,
          aggregationLabel: arrMrrLoading ? undefined : arrMrrLabel,
          formatAggregation: (value: number) => displayTotalCurrency(value),
          subtotalAggregations: [
            {
              key: "tcv",
              label: t("contracts.bottomKPI.tcv"),
              aggregate: (rows) =>
                rows.reduce(
                  (acc, row) => acc + (row.totalContractValue || 0),
                  0
                ),
              fromSums: (sums) => sums.totalContractValue || 0,
              format: (value) => displayTotalCurrency(value)
            },
            {
              key: "acv",
              label: t("contracts.bottomKPI.acv"),
              aggregate: (rows) =>
                rows.reduce(
                  (acc, row) => acc + (row.annualContractValue || 0),
                  0
                ),
              fromSums: (sums) => sums.annualContractValue || 0,
              format: (value) => displayTotalCurrency(value)
            },
            // Active basis only, and no basis at all until the setting resolves.
            ...(arrMrrLoading
              ? []
              : ([
                  {
                    key: isArr ? ContractColumn.ARR : ContractColumn.MRR,
                    label: isArr
                      ? t("contracts.bottomKPI.arr")
                      : t("contracts.bottomKPI.mrr"),
                    aggregate: (rows) =>
                      rows.reduce(
                        (acc, row) => acc + ((isArr ? row.arr : row.mrr) || 0),
                        0
                      ),
                    fromSums: (sums) => sums[isArr ? "arr" : "mrr"] || 0,
                    format: (value) => displayTotalCurrency(value)
                  },
                  {
                    key: isArr ? "avgArr" : "avgMrr",
                    label: isArr
                      ? t("contracts.bottomKPI.avgArr")
                      : t("contracts.bottomKPI.avgMrr"),
                    // Averages only rows carrying a value, not the whole group.
                    aggregate: (rows) => {
                      const values = rows
                        .map((row) => (isArr ? row.arr : row.mrr))
                        .filter((value) => typeof value === "number");
                      if (values.length === 0) return 0;
                      return (
                        values.reduce((acc, value) => acc + value, 0) /
                        values.length
                      );
                    },
                    fromSums: (sums, _count, counts) => {
                      const key = isArr ? "arr" : "mrr";
                      return counts[key] ? (sums[key] || 0) / counts[key] : 0;
                    },
                    format: (value) => displayTotalCurrency(value)
                  },
                  {
                    key: isArr ? ContractColumn.CARR : ContractColumn.CMRR,
                    label: isArr
                      ? t("contracts.bottomKPI.carr")
                      : t("contracts.bottomKPI.cmrr"),
                    aggregate: (rows) =>
                      rows.reduce(
                        (acc, row) =>
                          acc + ((isArr ? row.carr : row.cmrr) || 0),
                        0
                      ),
                    fromSums: (sums) => sums[isArr ? "carr" : "cmrr"] || 0,
                    format: (value) => displayTotalCurrency(value)
                  }
                ] satisfies SubtotalAggregation[]))
          ],
          groups: knownGroups,
          groupSummaries
        }}
        selectedView={selectedView}
        columnsState={columnsState}
        onRowClick={(row) => onRowClick(row.original as ContractTableRow)}
      />
      <NewQuickView
        isOpen={drawerState.isOpen}
        entityId={drawerState.entityId}
        entityType={EntityType.CONTRACT}
        onClose={closeQuickView}
      />
    </>
  );
};

export default ContractsTable;
